// Systém → Náklady AI (17.7.2026) — přehled útraty za VŠECHNU AI:
// jídelníčky (aplikace + PDF, tabulka ai_usage) + úkoly přes Slack agenta
// Joky (slack_ukol_naklady) + případná další budoucí práce ("ostatní").
// Data z /api/ai-naklady. Owner only.
import React, { useEffect, useState } from "react";
import { Cpu, RefreshCw } from "lucide-react";
import GradientHeader from "../components/GradientHeader.jsx";
import { Stranka, ErrorNote, SkeletonCards } from "../components/ui.jsx";
import { apiFetch } from "../lib/api.js";

// Orientační přepočet na koruny — jen pro rychlou představu, faktura od
// Anthropic je v USD. Kurz uprav podle reality.
const KURZ_CZK_ZA_USD = 24;

const TYP_LABELS = {
  jidelnicek_app: "Jídelníček v aplikaci",
  jidelnicek_pdf: "Jídelníček PDF",
  ukoly_joky: "Úkoly (Joky)",
  "pondelni-report": "Pondělní report analytiky",
};

const fmtUsd = (x) => (x === null || x === undefined) ? "-" : `$${Number(x).toFixed(2)}`;
const fmtCzk = (x) => (x === null || x === undefined) ? "" : `≈ ${Math.round(Number(x) * KURZ_CZK_ZA_USD).toLocaleString("cs-CZ")} Kč`;
const fmtMesic = (m) => {
  const [y, mo] = String(m).split("-");
  const NAZVY = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
  return `${NAZVY[parseInt(mo, 10) - 1] || mo} ${y}`;
};
const fmtKdy = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? String(iso) : d.toLocaleString("cs-CZ", { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
      <div className="text-xs uppercase tracking-wide font-semibold text-slate-500">{label}</div>
      <div className="text-2xl font-black text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function NakladyAI() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const nacti = () => {
    setLoading(true); setErr(null);
    apiFetch("/api/ai-naklady")
      .then((r) => r.json())
      .then((j) => { if (j.ok) setData(j); else setErr(j.error || "Chyba"); })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(nacti, []);

  if (loading && !data) {
    return <Stranka sirka="uzka"><SkeletonCards count={4} /></Stranka>;
  }
  if (err) {
    return <Stranka sirka="uzka"><ErrorNote onRetry={nacti}>{err}</ErrorNote></Stranka>;
  }
  const t = data?.total || {};
  const kat = data?.kategorie || {};
  const maOstatni = (kat.ostatni?.behy || 0) > 0;

  return (
    <Stranka sirka="uzka">
      <GradientHeader
        title="Náklady AI"
        icon={Cpu}
        loading={loading}
        akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: nacti, disabled: loading }]}
      />

      {/* Celkem + rozpad na kategorie (jídelníčky / úkoly / případně ostatní) */}
      <div className={`grid grid-cols-1 sm:grid-cols-3 ${maOstatni ? "lg:grid-cols-4" : ""} gap-3`}>
        <StatCard label="Celkem utraceno" value={fmtUsd(t.usd)} sub={fmtCzk(t.usd)} />
        <StatCard label="Jídelníčky" value={fmtUsd(kat.jidelnicky?.usd ?? 0)} sub={`${kat.jidelnicky?.behy ?? 0}× vygenerováno, průměrně ${fmtUsd(data?.prumerUsd)}`} />
        <StatCard label="Úkoly (Joky)" value={fmtUsd(kat.ukoly?.usd ?? 0)} sub={`${kat.ukoly?.behy ?? 0} zpráv ve Slacku`} />
        {maOstatni && <StatCard label="Ostatní" value={fmtUsd(kat.ostatni?.usd ?? 0)} sub={`${kat.ostatni?.behy ?? 0} běhů`} />}
      </div>

      {/* Podle typu */}
      {(data?.podleTypu || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 font-bold text-slate-800 text-sm">Podle typu</div>
          <div className="-mx-1 overflow-x-auto px-1">
            <table className="w-full text-sm">
              <tbody>
                {data.podleTypu.map((r) => (
                  <tr key={r.typ} className="border-b border-slate-50 last:border-0">
                    <td className="px-4 py-2.5 font-medium text-slate-800">{TYP_LABELS[r.typ] || r.typ}</td>
                    <td className="px-4 py-2.5 text-right text-slate-500">{r.behy}×</td>
                    <td className="px-4 py-2.5 text-right text-slate-500">prům. {fmtUsd(r.prumerUsd)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{fmtUsd(r.usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Po měsících — s rozpadem jídelníčky / úkoly */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 font-bold text-slate-800 text-sm">Po měsících</div>
          <div className="-mx-1 overflow-x-auto px-1">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="px-4 py-1.5 font-semibold">Měsíc</th>
                  <th className="px-2 py-1.5 font-semibold text-right">Jídelníčky</th>
                  <th className="px-2 py-1.5 font-semibold text-right">Úkoly</th>
                  {maOstatni && <th className="hidden px-2 py-1.5 text-right font-semibold sm:table-cell">Ostatní</th>}
                  <th className="px-4 py-1.5 font-semibold text-right">Celkem</th>
                </tr>
              </thead>
              <tbody>
                {(data?.mesice || []).map((m) => (
                  <tr key={m.mesic} className="border-b border-slate-50 last:border-0">
                    <td className="px-4 py-2.5 font-medium text-slate-800 capitalize">{fmtMesic(m.mesic)}</td>
                    <td className="px-2 py-2.5 text-right text-slate-500">{fmtUsd(m.usdJidelnicky ?? 0)}</td>
                    <td className="px-2 py-2.5 text-right text-slate-500">{fmtUsd(m.usdUkoly ?? 0)}</td>
                    {maOstatni && <td className="hidden px-2 py-2.5 text-right text-slate-500 sm:table-cell">{fmtUsd(m.usdOstatni ?? 0)}</td>}
                    <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{fmtUsd(m.usd)}</td>
                  </tr>
                ))}
                {(data?.mesice || []).length === 0 && <tr><td colSpan={maOstatni ? 5 : 4} className="px-4 py-6 text-center text-slate-400">Zatím žádná data, vzniknou prvním vygenerováním jídelníčku nebo zprávou Jokymu.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        {/* Po letech — s rozpadem jídelníčky / úkoly */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 font-bold text-slate-800 text-sm">Po letech</div>
          <div className="-mx-1 overflow-x-auto px-1">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="px-4 py-1.5 font-semibold">Rok</th>
                  <th className="px-2 py-1.5 font-semibold text-right">Jídelníčky</th>
                  <th className="px-2 py-1.5 font-semibold text-right">Úkoly</th>
                  {maOstatni && <th className="hidden px-2 py-1.5 text-right font-semibold sm:table-cell">Ostatní</th>}
                  <th className="px-4 py-1.5 font-semibold text-right">Celkem</th>
                </tr>
              </thead>
              <tbody>
                {(data?.roky || []).map((y) => (
                  <tr key={y.rok} className="border-b border-slate-50 last:border-0">
                    <td className="px-4 py-2.5 font-medium text-slate-800">{y.rok}</td>
                    <td className="px-2 py-2.5 text-right text-slate-500">{fmtUsd(y.usdJidelnicky ?? 0)}</td>
                    <td className="px-2 py-2.5 text-right text-slate-500">{fmtUsd(y.usdUkoly ?? 0)}</td>
                    {maOstatni && <td className="px-2 py-2.5 text-right text-slate-500">{fmtUsd(y.usdOstatni ?? 0)}</td>}
                    <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{fmtUsd(y.usd)} <span className="font-normal text-slate-400">{fmtCzk(y.usd)}</span></td>
                  </tr>
                ))}
                {(data?.roky || []).length === 0 && <tr><td colSpan={maOstatni ? 5 : 4} className="px-4 py-6 text-center text-slate-400">Zatím žádná data.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Poslední běhy */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 font-bold text-slate-800 text-sm">Posledních 20 běhů AI</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="px-4 py-2 font-semibold">Kdy</th>
                <th className="px-4 py-2 font-semibold">Typ</th>
                <th className="hidden px-4 py-2 font-semibold md:table-cell">Klient</th>
                <th className="hidden px-4 py-2 text-right font-semibold sm:table-cell">Tokeny (in/out)</th>
                <th className="px-4 py-2 font-semibold text-right">Cena</th>
              </tr>
            </thead>
            <tbody>
              {(data?.posledni || []).map((b, i) => (
                <tr key={i} className={`border-b border-slate-50 last:border-0 ${b.ok === false ? "bg-rose-50/40" : ""}`}>
                  <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmtKdy(b.kdy)}{b.ok === false && <span className="ml-1.5 text-[11px] font-bold text-rose-600">CHYBA</span>}</td>
                  <td className="px-4 py-2.5 text-slate-600">{TYP_LABELS[b.typ] || b.typ}</td>
                  <td className="hidden px-4 py-2.5 font-medium text-slate-800 demo-sensitive md:table-cell">{b.klient}</td>
                  <td className="hidden whitespace-nowrap px-4 py-2.5 text-right text-slate-500 sm:table-cell">{(b.input_tokens / 1000).toFixed(0)}k / {(b.output_tokens / 1000).toFixed(0)}k</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{fmtUsd(b.usd)}</td>
                </tr>
              ))}
              {(data?.posledni || []).length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Zatím žádná generování.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-xs text-slate-400">
        Ceny jsou spočítané z tokenů podle ceníku Anthropic (haiku $1/$5, sonnet $3/$15 za 1M tokenů in/out).
        Přepočet na Kč je orientační (kurz {KURZ_CZK_ZA_USD} Kč/USD, konstanta v kódu). Sledují se generátory jídelníčků
        (aplikace i PDF) a Slack agent Joky (zadávání úkolů), vždycky od nasazení dané verze, zpětně data nejsou.
      </div>
    </Stranka>
  );
}
